import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import USPresenceMap, { REACH_METRICS } from '../components/USPresenceMap';
import { BTN_PRIMARY } from '../components/styles';
import usePageMeta from '../hooks/usePageMeta';

// Company history timeline.
const MILESTONES = [
  { year: '2015', title: 'First Prototype Developed', text: 'Developed the first working prototype, laying the groundwork for RangeSafe\u2019s stove safety technology.' },
  { year: '2017', title: 'First Family of Patents Granted', text: 'Established the foundation of RangeSafe\u2019s intellectual property portfolio.' },
  { year: '2018', title: 'RangeSafe, LLC Founded', text: 'Formed the company to advance stove safety through product innovation.' },
  { year: '2019', title: 'Series A Raise Completed', text: 'Secured external investor funding to support the company\u2019s long-term development and growth.' },
  { year: '2019', title: 'First Government Contract Secured', text: 'Installed in overseas U.S. State Department housing.' },
  { year: '2020', title: 'Recognized by Good Housekeeping', text: 'Included in Good Housekeeping Magazine\u2019s \u201cGenius Parenting Solutions.\u201d' },
  { year: '2020', title: 'Official NFPA Fire Prevention Week Sponsor', text: 'Served as an official sponsor of the National Fire Protection Association\u2019s Fire Prevention Week.' },
  { year: '2021', title: 'Entered First Multi-National Commercial Hospitality Partnership', text: 'Secured multi-year contract with one of the world\u2019s leading hospitality groups.' },
  { year: '2021', title: 'First Full Fit-Out of Large-Scale Residential Property', text: 'Secured first multi-family apartment contract, extending reach into large-scale residential communities.' },
  { year: '2022', title: 'New York City Adopts Local Law 44 Regulation', text: 'Incorporated permanent stove safety knobs with integrated locking mechanisms as an approved solution to the city\u2019s amended stove safety requirements.' },
  { year: '2022', title: 'Ultimate Stove Safety Knob Introduced', text: 'Launched four-step knob with an additional safety feature, designed for children and adults with cognitive disabilities, satisfying the NYC Local Law 44 Regulation.' },
  { year: '2022', title: 'Rolled Out Manufacturer Partnership Options', text: 'Created commercial offering for stove manufacturers to either purchase RangeSafe knobs directly or license the company\u2019s technology to self-manufacture.' },
  { year: '2023', title: 'First Featured in Times Square on New Year\u2019s Eve', text: 'Showcased the RangeSafe brand on a video billboard in Times Square as part of a city-wide Local Law 44 awareness campaign.' },
  { year: '2024', title: 'Joined CPSC Working Group', text: 'Joined Consumer Product Safety Commission group tasked with identifying improvements to accidental actuation and stove safety.' },
  { year: '2026', title: 'First Nationwide Commercial Property Portfolio Rollout', text: 'Provided knobs for a major property management organization for installation in their residential buildings across the U.S.' },
  { year: '2026', title: 'Second Family of Patents Granted', text: 'Introduced additional models to RangeSafe\u2019s intellectual property portfolio.' },
];

const EXPERIENCE_ITEMS = [
  { title: 'Apartments & Condominiums', img: '/assets/industries/residential-ext.jpg', slug: 'residential' },
  { title: 'Single-Family Homes', img: '/assets/industries/experience-single-family.jpg', slug: 'residential' },
  { title: 'Public Housing', img: '/assets/industries/government-sunset2-c.jpg', slug: 'government' },
  { title: 'Government Buildings', img: '/assets/industries/experience-government-v2.jpg', slug: 'government' },
  { title: 'Military Accommodations', img: '/assets/industries/experience-military-v2.jpg', slug: 'government' },
  { title: 'Hotels & Timeshares', img: '/assets/industries/hospitality-sunset-b.jpg', slug: 'hospitality' },
  { title: 'Vacation Rentals', img: '/assets/industries/experience-vacation-rental.jpg', slug: 'hospitality' },
  { title: 'Short-Term Rentals', img: '/assets/industries/experience-short-term-rental.jpg', slug: 'hospitality' },
  { title: 'Educational Facilities', img: '/assets/industries/education-sunset2-c.jpg', slug: 'education' },
  { title: 'Assisted Living', img: '/assets/industries/senior-sunset2-c.jpg', slug: 'senior' },
  { title: 'Restaurants', img: '/assets/industries/restaurant-sunset.jpg', slug: 'restaurant' },
  { title: 'Recreational Vehicles', img: '/assets/industries/rv-sunset.jpg', slug: 'recreational-vehicles' },
];

// Hover flips the card to show the environment; clicking opens the matching industry page.
function ExperienceCard({ item, index }) {
  const [flipped, setFlipped] = useState(false);
  const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
  return (
    <Link
      to={`/industries/${item.slug}`}
      className="group block h-36 cursor-pointer [perspective:1000px] sm:h-40"
      data-testid={`experience-item-${index + 1}`}
      aria-label={`${item.title} — explore this industry`}
      onMouseEnter={canHover ? () => setFlipped(true) : undefined}
      onMouseLeave={canHover ? () => setFlipped(false) : undefined}
    >
      <div
        className={`relative h-full w-full transition-transform duration-500 [transform-style:preserve-3d] ${
          flipped ? '[transform:rotateY(180deg)]' : 'group-hover:[transform:rotateY(180deg)]'
        }`}
      >
        <div className="absolute inset-0 flex flex-col justify-center border border-line bg-panel/40 p-6 [backface-visibility:hidden] sm:p-7">
          <p className="font-mono text-[10px] tracking-[0.3em] text-accentText">{String(index + 1).padStart(2, '0')}</p>
          <h3 className="mt-3 font-display text-xl font-bold uppercase leading-tight tracking-tight text-white">
            {item.title}
          </h3>
          <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-mute">
            Tap or click to explore →
          </p>
        </div>
        <div className="absolute inset-0 overflow-hidden border border-line [transform:rotateY(180deg)] [backface-visibility:hidden]">
          <img src={item.img} alt="" aria-hidden="true" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/90">{item.title}</p>
          </div>
        </div>
      </div>
    </Link>
  );
}


const SUPPORT_COLUMNS = [
  {
    n: '01',
    t: 'Discuss Requirements',
    d: 'Tell us about your buildings, appliances and the outcomes you desire.',
  },
  {
    n: '02',
    t: 'Assess Compatibility',
    d: 'We review your appliance models to determine the RangeSafe Stove Safety Knob™ that will best address your concern.',
  },
  {
    n: '03',
    t: 'Evaluate a Pilot',
    d: 'Conduct a pre-purchase pilot program to confirm fit, operation and testing feedback.',
  },
  {
    n: '04',
    t: 'Plan Purchasing',
    d: 'Post-pilot program, we will help create a business case including models, quantities and pricing for management or Board review.',
  },
];

export default function About() {
  usePageMeta(
    'About RangeSafe | Stove Safety Knob Manufacturer',
    'RangeSafe makes Stove Safety Knobs™ — after-market stove knobs that require a Push, Press and Turn™ action, helping reduce accidental stove actuation.'
  );
  return (
    <main className="pt-16">
      {/* UNIFIED INTRODUCTION — text left, map right */}
      <section className="mx-auto max-w-shell px-5 pb-20 pt-20 sm:px-8 lg:pb-24 lg:pt-28" data-testid="company-overview">
        <SectionHead
          as="h1"
          eyebrow="The Company"
          lines={['Advancing stove safety', 'through patented innovation']}
        />
        <div className="mt-12 grid items-start gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-6 text-base leading-relaxed text-dim">
              <p>
                RangeSafe is the global leader in stove safety knob manufacturing, with products designed to help
                reduce unintended actuation of gas and electric stoves. We bring together proprietary
                technology, a diversified product portfolio and professional support to help commercial
                organizations integrate safety products into their properties and facilities.
              </p>
              <p>
                RangeSafe products have been implemented across industries, markets and countries,
                reflecting the broad need for stove safety in both residential and commercial settings.
                RangeSafe also provides products to stove manufacturers for direct integration during
                manufacturing, as well as technology licensing opportunities, if desired.
              </p>
            </div>
            <div className="mt-10 border border-line bg-panel/40 p-8 sm:p-10" data-testid="ada-compliance-box">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accentText">ADA Compliance</p>
              <p className="mt-5 text-base leading-relaxed text-dim">
                Safety and accessibility inform our approach to product design. RangeSafe Classic, Compact and
                Deluxe Stove Safety Knobs™ are designed to meet the operable-parts requirements of the ADA
                Standards for Accessible Design, Section 309.4—supporting one-handed operation without tight
                grasping, pinching or twisting of the wrist, with an activation force of no more than five
                pounds.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-3 divide-x divide-line border border-line border-b-0 bg-panel/40" data-testid="reach-metrics">
              {REACH_METRICS.map((m) => (
                <div key={m.label} className="px-3 py-5 text-center sm:px-6 sm:py-6">
                  <p className="font-display text-3xl font-bold text-white sm:text-4xl">{m.value}</p>
                  <p className="mt-1 font-mono text-[8px] uppercase leading-relaxed tracking-[0.18em] text-mute sm:text-[10px]">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
            <figure className="border border-line bg-panel/40">
              <div className="p-4 sm:p-6">
                <USPresenceMap />
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* COMPANY TIMELINE */}
      <section className="border-t border-line py-20 lg:py-24" data-testid="company-timeline">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <SectionHead lines={['Our history of', 'innovation']} />
          <div className="mt-14 overflow-x-auto pb-2" data-testid="timeline-track">
            <div className="relative flex w-max min-w-full gap-10 lg:gap-8">
              <div className="absolute left-0 right-0 top-[6px] h-px bg-line" aria-hidden="true" data-testid="timeline-line" />
              {MILESTONES.map((m, i) => (
                <Reveal key={m.year + m.title} delay={Math.min(i, 5) * 0.08} className="relative w-[220px] shrink-0 sm:w-[240px]">
                  <span className="relative block h-3.5 w-3.5 rounded-full bg-accent ring-4 ring-accent/15" aria-hidden="true" data-testid={`timeline-dot-${i + 1}`} />
                  <p className="mt-6 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">{m.year}</p>
                  <h3 className="mt-3 font-display text-base font-bold uppercase tracking-tight text-white sm:text-lg">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-dim">{m.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMERCIAL EXPERIENCE */}
      <section className="border-t border-line py-20 lg:py-24" data-testid="commercial-experience">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <SectionHead
            eyebrow="Commercial Experience"
            lines={['Experience across', 'commercial environments']}
            lead="RangeSafe products are installed in commercial settings throughout the U.S. and internationally, including the following categories:"
          />
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {EXPERIENCE_ITEMS.map((item, i) => (
              <Reveal key={item.title} delay={Math.min(i * 0.05, 0.2)}>
                <ExperienceCard item={item} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMERCIAL SUPPORT */}
      <section className="border-t border-line py-20 lg:py-24" data-testid="commercial-support">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <SectionHead
            eyebrow="Commercial Support"
            lines={['Support for', 'commercial decision making']}
            lead="RangeSafe Commercial Solutions offers a no cost, no obligation initial consultation to help prospective commercial clients assess which RangeSafe product is best suited for their needs and help develop pre-purchase pilot programs."
          />
          <div className="mt-14 grid gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-4">
            {SUPPORT_COLUMNS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06} className="bg-base">
                <div className="h-full p-7 sm:p-8" data-testid={`support-column-${i + 1}`}>
                  <p className="font-mono text-[11px] tracking-[0.3em] text-accentText">{c.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white">{c.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-dim">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CONTACT */}
      <section className="border-t border-line bg-panel/40 py-20 lg:py-28" data-testid="company-closing">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
              Discuss your organization’s
              <br />
              stove safety needs
            </h2>
            <div className="mt-10">
              <Link to="/contact" className={BTN_PRIMARY} data-testid="about-contact-cta">
                Contact Commercial Solutions Team
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
